import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';
import { v4 as uuidv4 } from 'uuid';
import { datadogRum } from '@datadog/browser-rum';

import { BaseApiConfig } from '~/lib/Api';
import { security } from '~/store/security';
import { isTestEnvironment } from '~/lib/util/environment.util';

const baseQuery = fetchBaseQuery({
  baseUrl: BaseApiConfig.baseUrl,
  prepareHeaders: async (headers) => {
    const access_token = await security.getAccessTokenSilently()({
      authorizationParams: {
        audience: BaseApiConfig.auth0Audience,
        scope: 'offline_access',
      },
    });

    if (access_token) {
      headers.set('Authorization', `Bearer ${access_token}`);
      headers.set('uniqueRequestId', `Maud:${uuidv4()}`);
    }
    return headers;
  },
  timeout: BaseApiConfig.timeout,
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 });

const baseQueryWithErrorLogging: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  try {
    const result = await baseQueryWithRetry(args, api, extraOptions);

    if (result.error) {
      const { error } = result;
      const errorData = error.data
        ? JSON.stringify(error.data)
        : JSON.stringify(error);

      // Report all errors to Datadog for observability
      datadogRum.addError(
        new Error(`API error: ${api.endpoint}`),
        {
          extra: {
            endpoint: api.endpoint,
            status: error.status,
            data: errorData,
          },
          tags: { logFrom: 'baseQueryWithOptions' },
        },
      );

      if (!isTestEnvironment) {
        // FETCH_ERROR indicates network/CORS issues - downgrade to warning
        // to reduce console noise for transient failures
        if (error.status === 'FETCH_ERROR') {
          console.warn(`API network error, url: ${api.endpoint}`, {
            data: errorData,
            endpoint: api.endpoint,
            status: error.status,
          });
        } else {
          // HTTP errors (4xx, 5xx) remain as errors for immediate visibility
          console.error(`Error executing API call, url: ${api.endpoint}`, {
            data: errorData,
            endpoint: api.endpoint,
            status: result.error.status,
          });
        }
      }
    }

    return result;
  } catch (error) {
    console.error(error, {
      endpoint: api.endpoint,
    });

    throw error;
  }
};

const baseQueryWithOptions = baseQueryWithErrorLogging;
export default baseQueryWithOptions;
